// Microsoft Clarity — deferred loader with queue-shim, mirroring src/utils/posthog.js.
//
// Design (same rationale as PostHog):
//   - The Clarity tag (~40 KB) is injected only AFTER the page `load` event
//     via requestIdleCallback so it never blocks first paint.
//   - Any clarityIdentify / clarityTag calls made BEFORE the tag finishes
//     loading are buffered here and flushed on `window.clarity` becoming
//     available. This means AuthContext can call clarityIdentify at login
//     time without needing to know whether Clarity is ready yet.
//   - Every function is a graceful no-op if `projectId` is empty or the
//     script fails to load. Analytics can never block a core flow.

let _initStarted = false;
let _ready = false;
const MAX_QUEUE = 200;
const _queue = [];

function _flush() {
  if (typeof window === 'undefined' || !window.clarity) return;
  while (_queue.length) {
    const item = _queue.shift();
    try {
      window.clarity(item.method, ...item.args);
    } catch {
      /* ignore */
    }
  }
}

function _enqueueOrCall(method, args) {
  if (_ready && typeof window !== 'undefined' && window.clarity) {
    try {
      window.clarity(method, ...args);
    } catch {
      /* ignore */
    }
    return;
  }
  if (_queue.length >= MAX_QUEUE) return;
  _queue.push({ method, args });
}

// Poll for `window.clarity` becoming available so the queue can drain.
// The Clarity snippet itself defines `window.clarity` synchronously, so this
// check flips true as soon as the script tag loads (usually within a few
// hundred ms of _scheduleDeferred running).
function _watchReady() {
  const start = Date.now();
  const iv = setInterval(() => {
    if (typeof window !== 'undefined' && window.clarity && window.clarity.q === undefined) {
      // `q` is only defined on the pre-load stub; its absence means the real
      // SDK has replaced it. Flush queued calls now.
      _ready = true;
      clearInterval(iv);
      _flush();
    } else if (Date.now() - start > 10_000) {
      // 10s timeout — SDK never loaded (blocked / offline). Give up so we
      // don't loop forever. Queued calls will simply be dropped.
      clearInterval(iv);
    }
  }, 200);
}

export function initClarity({ projectId } = {}) {
  if (_initStarted) return;
  _initStarted = true;
  if (!projectId) return; // Not configured — stay disabled; queued calls dropped.
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    // Standard Clarity snippet — installs a queue-shim at window.clarity
    // synchronously, then loads the real SDK which replaces the shim.
    /* eslint-disable */
    (function (c, l, a, r, i, t, y) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', projectId);
    /* eslint-enable */
    _watchReady();
  } catch {
    /* never break the app */
  }
}

// Public API — safe to call before init.
export function clarityIdentify(userId, email) {
  if (!userId) return;
  _enqueueOrCall('identify', [userId, undefined, undefined, email]);
}

export function clarityTag(key, value) {
  if (!key) return;
  _enqueueOrCall('set', [key, value]);
}

// Test hook — inspect queue depth in tests.
export function _clarityQueueLengthForTesting() {
  return _queue.length;
}
